import * as Redux from '@reduxjs/toolkit';
import type { DSXSelectOption } from '~/components/DSX/Select';
import * as Hub from '~/ogm-hub';

export interface IGlobalActivityState {
	productActivities: Hub.ProductActivity[];
	activityTypes: number[];
	blueprints: Hub.IdAndName[];
	users: Hub.IdAndName[];
	unitMeasureCodes: string[];
	dates: number[];
	totalResults: number;
	actionsAreLoading: boolean;
	searchText: string;
	currentPage: number;
	pageSize: number;
	filter: {
		isFilterModalOpen: boolean;
		activityTypesSelected: DSXSelectOption[];
		blueprintsSelected: DSXSelectOption[];
		usersSelected: DSXSelectOption[];
		unitMeasuresSelected: DSXSelectOption[];
		daysSelected: DSXSelectOption | null;
	};
}

export function makeInitialState(): IGlobalActivityState {
	return {
		productActivities: [],
		activityTypes: [],
		blueprints: [],
		users: [],
		unitMeasureCodes: [],
		dates: [1, 7, 30, 90, 180, 270],
		totalResults: 0,
		actionsAreLoading: false,
		searchText: '',
		currentPage: 0,
		pageSize: 50,
		filter: {
			isFilterModalOpen: false,
			activityTypesSelected: [],
			blueprintsSelected: [],
			usersSelected: [],
			unitMeasuresSelected: [],
			daysSelected: null,
		},
	};
}

export enum ActivityFilterType {
	ActivityType,
	Blueprint,
	User,
	UnitMeasure,
	Date,
}

export const slice = Redux.createSlice({
	name: 'globalActivity',
	initialState: makeInitialState(),
	reducers: {
		resetState(state) {
			const initialState = makeInitialState();
			state.productActivities = initialState.productActivities;
			state.activityTypes = initialState.activityTypes;
			state.blueprints = initialState.blueprints;
			state.users = initialState.users;
			state.unitMeasureCodes = initialState.unitMeasureCodes;
			state.dates = initialState.dates;
			state.totalResults = initialState.totalResults;
			state.actionsAreLoading = initialState.actionsAreLoading;
			state.searchText = initialState.searchText;
			state.currentPage = initialState.currentPage;
			state.pageSize = initialState.pageSize;
			state.filter = initialState.filter;
		},
		clearActions(state) {
			state.productActivities = makeInitialState().productActivities;
			state.totalResults = makeInitialState().totalResults;
		},
		searchInputUpdate(state, action) {
			state.searchText = action.payload;
		},
		searchChange(state, action) {
			state.searchText = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
		pageChange(state, action) {
			state.currentPage = action.payload;
		},
		loadingActions(state) {
			state.actionsAreLoading = true;
		},
		loadingActions_SUCCESS(
			state,
			action: Redux.PayloadAction<Hub.GetProductActivityResponse>
		) {
			state.actionsAreLoading = false;
			state.productActivities = action.payload.productActivities;
			state.activityTypes = action.payload.activityTypes;
			state.blueprints = action.payload.blueprints;
			state.users = action.payload.users;
			state.unitMeasureCodes = action.payload.unitMeasureCodes;
			state.totalResults = action.payload.totalResults;
		},
		loadingActions_SUCCESS_NoFilterCategoriesReset(
			state,
			action: Redux.PayloadAction<Hub.GetProductActivityResponse>
		) {
			state.actionsAreLoading = false;
			state.productActivities = action.payload.productActivities;
			state.users = action.payload.users;
			state.blueprints = action.payload.blueprints;
			state.totalResults = action.payload.totalResults;
		},
		loadingActions_ERROR(state) {
			state.actionsAreLoading = false;
		},
		filterModalOpened(state) {
			state.filter.isFilterModalOpen = true;
		},
		filterModalClosed(state) {
			state.filter.isFilterModalOpen = false;
		},
		activityTypeFilterChange(state, action) {
			state.filter.activityTypesSelected = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
		blueprintFilterChange(state, action) {
			state.filter.blueprintsSelected = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
		userFilterChange(state, action) {
			state.filter.usersSelected = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
		unitMeasureFilterChange(state, action) {
			state.filter.unitMeasuresSelected = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
		dateFilterChange(state, action) {
			state.filter.daysSelected = action.payload;
			state.currentPage = makeInitialState().currentPage;
		},
	},
});

export const thunks = {
	globalActivityPageLoaded() {
		return async (dispatch, getState) => {
			dispatch(slice.actions.resetState());
			dispatch(slice.actions.loadingActions());

			const state = getState().globalActivity;

			const params: Hub.GetProductActivityRequest = {
				productActivityTypes: state.filter.activityTypesSelected.map(
					(x) => x.value
				),
				userIds: state.filter.usersSelected.map((x) => x.value),
				blueprintIds: state.filter.blueprintsSelected.map((x) => x.value),
				supplierProductNumber: state.searchText,
				unitMeasureCodes: state.filter.unitMeasuresSelected.map((x) => x.value),
				days: state.filter.daysSelected?.value,
				skip: 0,
				take: state.pageSize,
			};
			const productActivityRes =
				await Hub.ActivityController.GetProductActivity(params);

			if (!productActivityRes.ok) {
				dispatch(slice.actions.loadingActions_ERROR());
				return;
			}
			dispatch(slice.actions.loadingActions_SUCCESS(productActivityRes.value));
		};
	},
	globalActivitySearchInputUpdate(text?: string) {
		return async (dispatch) => {
			dispatch(slice.actions.searchInputUpdate(text));
		};
	},
	globalActivitySearch(text?: string) {
		return async (dispatch, getState) => {
			dispatch(slice.actions.clearActions());
			dispatch(slice.actions.loadingActions());
			dispatch(slice.actions.searchChange(text));

			const state = getState().globalActivity;

			const params: Hub.GetProductActivityRequest = {
				productActivityTypes: state.filter.activityTypesSelected.map(
					(x) => x.value
				),
				userIds: state.filter.usersSelected.map((x) => x.value),
				blueprintIds: state.filter.blueprintsSelected.map((x) => x.value),
				supplierProductNumber: text,
				unitMeasureCodes: state.filter.unitMeasuresSelected.map((x) => x.value),
				days: state.filter.daysSelected?.value,
				skip: 0,
				take: state.pageSize,
			};
			const productActivityRes =
				await Hub.ActivityController.GetProductActivity(params);

			if (!productActivityRes.ok) {
				dispatch(slice.actions.loadingActions_ERROR());
				return;
			}
			dispatch(slice.actions.loadingActions_SUCCESS(productActivityRes.value));
		};
	},
	globalActivityPageChange(page: number) {
		return async (dispatch, getState) => {
			dispatch(slice.actions.clearActions());
			dispatch(slice.actions.loadingActions());
			dispatch(slice.actions.pageChange(page));

			const state = getState().globalActivity;

			const params: Hub.GetProductActivityRequest = {
				productActivityTypes: state.filter.activityTypesSelected.map(
					(x) => x.value
				),
				userIds: state.filter.usersSelected.map((x) => x.value),
				blueprintIds: state.filter.blueprintsSelected.map((x) => x.value),
				supplierProductNumber: state.searchText,
				unitMeasureCodes: state.filter.unitMeasuresSelected.map((x) => x.value),
				days: state.filter.daysSelected?.value,
				skip: page * state.pageSize,
				take: state.pageSize,
			};
			const productActivityRes =
				await Hub.ActivityController.GetProductActivity(params);

			if (!productActivityRes.ok) {
				dispatch(slice.actions.loadingActions_ERROR());
				return;
			}
			dispatch(slice.actions.loadingActions_SUCCESS(productActivityRes.value));
		};
	},
	globalActivityFilterChange(args: {
		filterValue: any;
		filterType: ActivityFilterType;
	}) {
		return async (dispatch, getState) => {
			switch (args.filterType) {
				case ActivityFilterType.ActivityType:
					dispatch(slice.actions.activityTypeFilterChange(args.filterValue));
					break;
				case ActivityFilterType.Blueprint:
					dispatch(slice.actions.blueprintFilterChange(args.filterValue));
					break;
				case ActivityFilterType.User:
					dispatch(slice.actions.userFilterChange(args.filterValue));
					break;
				case ActivityFilterType.UnitMeasure:
					dispatch(slice.actions.unitMeasureFilterChange(args.filterValue));
					break;
				case ActivityFilterType.Date:
					dispatch(slice.actions.dateFilterChange(args.filterValue));
					break;
				default:
					return;
			}
			dispatch(slice.actions.clearActions());
			dispatch(slice.actions.loadingActions());
			dispatch(slice.actions.pageChange(0));

			const state = getState().globalActivity;

			const params: Hub.GetProductActivityRequest = {
				productActivityTypes: state.filter.activityTypesSelected.map(
					(x) => x.value
				),
				userIds: state.filter.usersSelected.map((x) => x.value),
				blueprintIds: state.filter.blueprintsSelected.map((x) => x.value),
				supplierProductNumber: state.searchText,
				unitMeasureCodes: state.filter.unitMeasuresSelected.map((x) => x.value),
				days: state.filter.daysSelected?.value,
				skip: 0,
				take: state.pageSize,
			};
			const productActivityRes =
				await Hub.ActivityController.GetProductActivity(params);

			if (!productActivityRes.ok) {
				dispatch(slice.actions.loadingActions_ERROR());
				return;
			}
			dispatch(
				slice.actions.loadingActions_SUCCESS_NoFilterCategoriesReset(
					productActivityRes.value
				)
			);
		};
	},
};
